export interface Owner {
  id?: string;
  name: { name: string; value: string } | null;
  percentage: number;
  status: "ACTIVE" | "INACTIVE";
}

/**
 * Converts the complex ownership data structure to a simpler format for form handling
 * @param data - The complex ownership data array from the API
 * @returns Simplified owner objects array
 */
export const convertToOwnershipData = (data: any[]): Owner[] => {
  if (!Array.isArray(data)) return [];

  return data.map((item) => {
    const nameField = item.nestedfieldList?.find(
      (field: any) => field.id === "name"
    );
    const percentageField = item.nestedfieldList?.find(
      (field: any) => field.id === "percentage"
    );

    return {
      id: item.id || ``,
      name: nameField?.value || "",
      percentage: percentageField?.value || 0
    };
  });
};

/**
 * Converts simplified owner objects back to the complex API structure
 * @param owners - Array of simplified owner objects
 * @returns Complex ownership data structure for API submission
 */
export const convertFromOwnershipData = (owners: Owner[]): any[] => {
  return owners.map((owner, index) => ({
    id: owner.id || "",
    label: `Owner ${index + 1}`,
    value: "",
    formattedValue: null,
    nestedfieldList: [
      {
        id: "name",
        label: "Name",
        isJsonStr: true,
        jsonKey: `.loanApplication.user.dynamicUserInfoJson.ownerDetails[%d].name`,
        value: owner.name,
        mandatory: {},
        editable: {
          value: null,
          isEditable: true
        },
        visible: {
          value: "",
          isVisible: true
        }
      },
      {
        id: "percentage",
        label: "Percentage",
        isJsonStr: true,
        jsonKey: `.loanApplication.user.dynamicUserInfoJson.ownerDetails[%d].percentage`,
        value: owner.percentage,
        mandatory: {},
        editable: {
          value: null,
          isEditable: true
        },
        visible: {
          value: "",
          isVisible: true
        }
      }
    ],
    tag: {
      visible: {
        value: null,
        isVisible: false
      },
      value: null
    },
    visible: {
      value: "",
      isVisible: true
    },
    mandatory: {},
    editable: {
      value: null,
      isEditable: true
    }
  }));
};

/**
 * Validates that the total ownership percentage equals 100%
 * @param owners - Array of owner objects
 * @returns true if valid, error message if invalid
 */
export const validateTotalPercentage = (owners: Owner[]): true | string => {
  const total = owners.reduce((sum, owner) => sum + (owner.percentage || 0), 0);
  if (Math.abs(total - 100) > 0.01) {
    // Allow for small floating point differences
    return "Total percentage must equal 100%";
  }
  return true;
};

/**
 * Calculates the total ownership percentage
 * @param owners - Array of owner objects
 * @returns Total percentage as a number
 */
export const calculateTotalPercentage = (owners: Owner[]): number => {
  return owners.reduce((sum, owner) => sum + (owner.percentage || 0), 0);
};

/**
 * Validates individual owner percentage
 * @param percentage - Percentage value to validate
 * @returns true if valid, error message if invalid
 */
export const validateOwnerPercentage = (percentage: number): true | string => {
  if (percentage < 0) return "Percentage cannot be negative";
  if (percentage > 100) return "Percentage cannot exceed 100%";
  return true;
};

/**
 * Creates a new owner object with default values
 * @param index - Index for the new owner
 * @returns New owner object
 */
export const createNewOwner = (): Owner => {
  return {
    // id: `owner-${Date.now()}-${index}`,
    name: "",
    percentage: 0
  };
};
